(() => {
  const roots = new WeakSet();

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency', currency: currency || 'CAD'
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  function initGallery(root) {
    const gallery = root.querySelector('[data-pklp-gallery]');
    const mainElement = gallery?.querySelector('[data-pklp-gallery-main]');
    if (!gallery || !mainElement || typeof window.Swiper !== 'function') return false;

    const thumbsElement = gallery.querySelector('[data-pklp-gallery-thumbs]');
    const desktopBreak = 1100;

    /* Determine if the gallery is wide enough for vertical thumbs */
    const isDesktopLayout = () => {
      const width = gallery.offsetWidth || window.innerWidth;
      return width >= desktopBreak || window.innerWidth >= desktopBreak;
    };

    const thumbsBaseConfig = {
      slidesPerView: 'auto',
      spaceBetween: 10,
      watchSlidesProgress: true,
      freeMode: true,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      updateOnWindowResize: true,
      slideToClickedSlide: true
    };

    let thumbs = null;
    let lastThumbDirection = null;

    const createThumbs = () => {
      const dir = isDesktopLayout() ? 'vertical' : 'horizontal';
      if (thumbs && lastThumbDirection === dir) return thumbs;
      if (thumbs && !thumbs.destroyed) thumbs.destroy(true, true);
      lastThumbDirection = dir;
      thumbs = new window.Swiper(thumbsElement, {
        ...thumbsBaseConfig,
        direction: dir
      });
      return thumbs;
    };

    if (thumbsElement) createThumbs();

    const main = new window.Swiper(mainElement, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 380,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      updateOnWindowResize: true,
      watchOverflow: true,
      keyboard: { enabled: true },
      navigation: {
        prevEl: gallery.querySelector('[data-pklp-gallery-prev]'),
        nextEl: gallery.querySelector('[data-pklp-gallery-next]')
      },
      pagination: {
        el: gallery.querySelector('[data-pklp-gallery-count]'),
        type: 'fraction'
      },
      thumbs: thumbs ? { swiper: thumbs } : undefined,
      on: {
        slideChange() {
          if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
          const activeIndex = this.activeIndex;
          gallery.querySelectorAll('.pklp-gallery__slide').forEach((slide, index) => {
            slide.classList.toggle('is-active', index === activeIndex);
          });
          if (thumbsElement) {
            thumbsElement.querySelectorAll('.swiper-slide').forEach((thumb, index) => {
              thumb.classList.toggle('swiper-slide-thumb-active', index === activeIndex);
              thumb.classList.toggle('is-active', index === activeIndex);
            });
          }
        }
      }
    });

    /* Attach click event directly to each thumbnail slide for instant slide transition */
    if (thumbsElement) {
      thumbsElement.querySelectorAll('.swiper-slide').forEach((thumbSlide, index) => {
        thumbSlide.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          main.slideTo(index, 350);
          thumbs?.slideTo(Math.max(index - 1, 0), 350);
        });
      });
    }

    /* Re-create the thumbs Swiper when crossing the desktop breakpoint */
    if (thumbsElement) {
      const onResize = () => {
        const needed = isDesktopLayout() ? 'vertical' : 'horizontal';
        if (needed === lastThumbDirection) return;
        const activeIndex = main.activeIndex || 0;
        createThumbs();
        main.thumbs.swiper = thumbs;
        main.thumbs.init();
        main.thumbs.update(true);
        thumbs.slideTo(Math.max(activeIndex - 1, 0), 0);
      };
      window.addEventListener('resize', onResize, { passive: true });
    }

    /* Variant change slider trigger */
    gallery.pklpMainSwiper = main;
    gallery.setActiveMedia = (mediaId) => {
      if (!mediaId) return;
      main.update();
      thumbs?.update();
      const cleanId = String(mediaId).split('-').pop();
      const slides = Array.from(mainElement.querySelectorAll('.pklp-gallery__slide'));
      const index = slides.findIndex((slide) => {
        const sid = slide.dataset.mediaId || slide.dataset.target || '';
        return sid === mediaId || sid.endsWith(`-${cleanId}`) || sid === cleanId;
      });
      if (index < 0) return;
      main.slideTo(index, 350);
      thumbs?.slideTo(Math.max(index - 1, 0), 350);
    };

    /* Initial active slide sync */
    const initialActive = mainElement.querySelector('.pklp-gallery__slide.is-active');
    if (initialActive) {
      const initialIndex = Array.from(mainElement.querySelectorAll('.pklp-gallery__slide')).indexOf(initialActive);
      if (initialIndex > 0) {
        main.slideTo(initialIndex, 0);
        thumbs?.slideTo(Math.max(initialIndex - 1, 0), 0);
      }
    }

    return true;
  }

  function initUgc(root) {
    const element = root.querySelector('[data-pklp-ugc]');
    if (!element || typeof window.Swiper !== 'function') return false;
    new window.Swiper(element, {
      slidesPerView: 1.25,
      spaceBetween: 14,
      speed: 420,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      updateOnWindowResize: true,
      navigation: {
        prevEl: root.querySelector('[data-pklp-ugc-prev]'),
        nextEl: root.querySelector('[data-pklp-ugc-next]')
      },
      breakpoints: {
        520: { slidesPerView: 2.1, spaceBetween: 16 },
        750: { slidesPerView: 3, spaceBetween: 18 }
      },
      on: { slideChange() { root.querySelectorAll('.pklp__ugc-card video').forEach((video) => video.pause()); } }
    });
    return true;
  }

  function initUpsells(root) {
    const element = root.querySelector('[data-pklp-upsells]');
    if (!element || typeof window.Swiper !== 'function') return false;
    new window.Swiper(element, {
      slidesPerView: 1,
      spaceBetween: 10,
      speed: 360,
      autoHeight: true,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      updateOnWindowResize: true
    });
    return true;
  }

  function updateSwipers(root) {
    root.querySelectorAll('.swiper').forEach((element) => {
      if (!element.swiper || element.swiper.destroyed) return;
      element.swiper.update();
      element.swiper.updateSize();
      element.swiper.updateSlides();
      element.swiper.updateProgress();
    });
  }

  function scheduleSwiperUpdate(root) {
    if (root.pklpSwiperUpdateFrame) window.cancelAnimationFrame(root.pklpSwiperUpdateFrame);
    if (root.pklpSwiperUpdateTimer) window.clearTimeout(root.pklpSwiperUpdateTimer);
    root.pklpSwiperUpdateFrame = window.requestAnimationFrame(() => {
      root.pklpSwiperUpdateFrame = window.requestAnimationFrame(() => {
        root.pklpSwiperUpdateFrame = null;
        updateSwipers(root);
      });
    });
    root.pklpSwiperUpdateTimer = window.setTimeout(() => {
      root.pklpSwiperUpdateTimer = null;
      updateSwipers(root);
    }, 180);
  }

  async function refreshShipping(root, eventCartData) {
    const shipping = root.querySelector('[data-pklp-shipping]');
    if (!shipping) return;
    const threshold = Number(shipping.dataset.thresholdCents || 0);
    if (!threshold) return;
    try {
      let cart = eventCartData;
      if (!cart || typeof cart.total_price === 'undefined') {
        const response = await fetch(`${window.routes?.cart_url || '/cart'}.js?v=${Date.now()}`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        if (!response.ok) return;
        cart = await response.json();
      }
      const totalPrice = Number(cart.total_price || 0);
      const remaining = Math.max(threshold - totalPrice, 0);
      const percent = Math.min((totalPrice / threshold) * 100, 100);
      const message = shipping.querySelector('[data-pklp-shipping-message]');
      const bar = shipping.querySelector('[data-pklp-shipping-bar]');
      const currency = cart.currency || root.dataset.currency || '$';
      if (message) {
        message.textContent = remaining > 0
          ? shipping.dataset.beforeText.replace('[amount]', money(remaining, currency))
          : shipping.dataset.completeText;
      }
      if (bar) {
        bar.style.width = `${percent}%`;
      }
    } catch (_) { }
  }

  function syncSticky(root) {
    const sticky = root.querySelector('[data-pklp-sticky]');
    if (!sticky) return;
    const primary = root.querySelector('[data-pklp-main-atc] .product-form__submit') || root.querySelector('.product-form__submit');
    if (!primary) return;
    const rect = primary.getBoundingClientRect();
    const isPast = rect.bottom < 0;
    sticky.classList.toggle('is-visible', isPast);
    sticky.toggleAttribute('aria-hidden', !isPast);
  }

  function initRoot(root, attempt = 0) {
    if (roots.has(root)) return;
    if (typeof window.Swiper !== 'function' && attempt < 80) {
      window.setTimeout(() => initRoot(root, attempt + 1), 50);
      return;
    }
    roots.add(root);
    initGallery(root);
    initUgc(root);
    initUpsells(root);
    scheduleSwiperUpdate(root);

    if (typeof window.ResizeObserver === 'function') {
      const resizeObserver = new ResizeObserver(() => {
        scheduleSwiperUpdate(root);
        syncSticky(root);
      });
      resizeObserver.observe(root);
      root.pklpResizeObserver = resizeObserver;
    }

    root.querySelectorAll('[data-pklp-bundle]').forEach((button) => {
      button.addEventListener('click', () => {
        root.querySelectorAll('[data-pklp-bundle]').forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-selected', active);
          item.setAttribute('aria-checked', active ? 'true' : 'false');
        });
        const quantity = root.querySelector('[data-pklp-quantity-input]');
        if (quantity) {
          quantity.value = button.dataset.quantity || '1';
          quantity.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    root.querySelector('[data-pklp-sticky-button]')?.addEventListener('click', () => {
      const atc = root.querySelector('[data-pklp-main-atc] .product-form__submit:not([disabled])') || root.querySelector('.product-form__submit:not([disabled])');
      if (atc) {
        atc.click();
      } else {
        root.querySelector('.pklp__buy-panel')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    root.querySelector('[data-pklp-closing-button]')?.addEventListener('click', () => {
      document.querySelector('[data-pklp-main-atc] .product-form__submit:not([disabled])')?.click();
    });

    root.querySelectorAll('[data-pklp-quick-add]').forEach((button) => {
      button.addEventListener('click', async () => {
        const variantId = button.dataset.variantId;
        if (!variantId || button.disabled) return;
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        if (cart) {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement?.(button);
        }
        button.disabled = true;
        button.classList.add('loading');
        try {
          const config = typeof window.fetchConfig === 'function'
            ? window.fetchConfig('javascript')
            : { method: 'POST', headers: { Accept: 'application/json' } };
          if (config.headers) delete config.headers['Content-Type'];
          config.body = formData;
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', config);
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || 'Could not add this item.');
          cart?.renderContents?.(result);
          const publishFn = typeof publish === 'function' ? publish : (typeof window.publish === 'function' ? window.publish : null);
          const pubSubEvents = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : (typeof window.PUB_SUB_EVENTS !== 'undefined' ? window.PUB_SUB_EVENTS : null);
          if (publishFn) {
            publishFn(pubSubEvents?.cartUpdate || 'cart-update', { source: 'pawkoda-upsell', productVariantId: variantId, cartData: result });
          }
          refreshShipping(root, result);
        } catch (error) {
          console.error(error);
        } finally {
          button.disabled = false;
          button.classList.remove('loading');
        }
      });
    });

    const stickyPrice = root.querySelector('[data-pklp-sticky-price]');
    const price = root.querySelector(`[id^="price-"]`);
    if (stickyPrice && price) {
      const update = () => { stickyPrice.textContent = price.querySelector('.price-item--regular, .price-item--sale')?.textContent.trim() || ''; };
      new MutationObserver(update).observe(price, { childList: true, subtree: true, characterData: true });
      update();
    }

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { syncSticky(root); ticking = false; });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();

    const subscribeFn = typeof subscribe === 'function' ? subscribe : (typeof window.subscribe === 'function' ? window.subscribe : null);
    const pubSubEvents = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : (typeof window.PUB_SUB_EVENTS !== 'undefined' ? window.PUB_SUB_EVENTS : null);

    const cartUpdateEvent = pubSubEvents?.cartUpdate || 'cart-update';
    if (subscribeFn) {
      subscribeFn(cartUpdateEvent, (event) => {
        refreshShipping(root, event?.cartData);
      });
    }

    const variantChangeEvent = pubSubEvents?.variantChange || 'variant-change';
    if (subscribeFn && root.dataset.section) {
      subscribeFn(variantChangeEvent, ({ data }) => {
        if (String(data?.sectionId) !== String(root.dataset.section) || !data?.variant) return;
        const basePrice = Number(data.variant.price || 0);
        const currency = root.dataset.currency;

        /* Update Single price */
        root.querySelectorAll('[data-pklp-bundle-price]').forEach((price) => {
          price.textContent = money(basePrice * Number(price.dataset.multiplier || 1), currency);
        });

        /* Update Pair pricing */
        root.querySelectorAll('[data-pklp-pair-total]').forEach((pairTotal) => {
          const discount = Number(pairTotal.dataset.discount || 0);
          const regularTotal = basePrice * 2;
          const finalTotal = discount > 0 ? Math.round(regularTotal * (1 - discount / 100)) : regularTotal;
          pairTotal.textContent = money(finalTotal, currency);
        });
        root.querySelectorAll('[data-pklp-pair-regular]').forEach((pairReg) => {
          pairReg.textContent = money(basePrice * 2, currency);
        });
        root.querySelectorAll('[data-pklp-pair-unit]').forEach((pairUnit) => {
          const pairTotalEl = root.querySelector('[data-pklp-pair-total]');
          const discount = Number(pairTotalEl?.dataset.discount || 0);
          const regularTotal = basePrice * 2;
          const finalTotal = discount > 0 ? Math.round(regularTotal * (1 - discount / 100)) : regularTotal;
          pairUnit.textContent = money(Math.round(finalTotal / 2), currency);
        });

        /* Update Trio pricing */
        root.querySelectorAll('[data-pklp-trio-total]').forEach((trioTotal) => {
          const discount = Number(trioTotal.dataset.discount || 0);
          const regularTotal = basePrice * 3;
          const finalTotal = discount > 0 ? Math.round(regularTotal * (1 - discount / 100)) : regularTotal;
          trioTotal.textContent = money(finalTotal, currency);
        });
        root.querySelectorAll('[data-pklp-trio-regular]').forEach((trioReg) => {
          trioReg.textContent = money(basePrice * 3, currency);
        });
        root.querySelectorAll('[data-pklp-trio-unit]').forEach((trioUnit) => {
          const trioTotalEl = root.querySelector('[data-pklp-trio-total]');
          const discount = Number(trioTotalEl?.dataset.discount || 0);
          const regularTotal = basePrice * 3;
          const finalTotal = discount > 0 ? Math.round(regularTotal * (1 - discount / 100)) : regularTotal;
          trioUnit.textContent = money(Math.round(finalTotal / 3), currency);
        });
      });
    }

    /* Initialize quantity based on pre-selected bundle option */
    const selectedBundle = root.querySelector('[data-pklp-bundle].is-selected');
    if (selectedBundle && selectedBundle.dataset.quantity && selectedBundle.dataset.quantity !== '1') {
      const quantityInput = root.querySelector('[data-pklp-quantity-input]');
      if (quantityInput) {
        quantityInput.value = selectedBundle.dataset.quantity;
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* Listen for DOM cart events */
    document.addEventListener('cart:updated', (e) => refreshShipping(root, e?.detail?.cart));
    document.addEventListener('cart:change', (e) => refreshShipping(root, e?.detail?.cart));
    document.addEventListener('cart:refresh', () => refreshShipping(root));

    /* MutationObserver on cart drawer / notification so that any cart DOM change triggers update */
    const cartObserverTarget = document.querySelector('cart-drawer, cart-notification, [id^="CartDrawer"]');
    if (cartObserverTarget) {
      new MutationObserver(() => refreshShipping(root)).observe(cartObserverTarget, { childList: true, subtree: true });
    }

    refreshShipping(root);
  }

  function boot(scope = document) {
    if (scope.matches?.('[data-pklp-root]')) initRoot(scope);
    scope.querySelectorAll('[data-pklp-root]').forEach((root) => initRoot(root));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target);
    event.target.querySelectorAll?.('[data-pklp-root]').forEach((root) => scheduleSwiperUpdate(root));
  });
  document.addEventListener('shopify:section:select', (event) => {
    boot(event.target);
    event.target.querySelectorAll?.('[data-pklp-root]').forEach((root) => scheduleSwiperUpdate(root));
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.('[data-pklp-root]');
    if (root) scheduleSwiperUpdate(root);
  });
})();
